import React, { useState, useEffect } from "react";
import api from "../services/api";
import { Sparkles, Flame, Trophy, Zap, Star, Loader2, CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { jwtDecode } from "jwt-decode";
import { getToken } from "../services/auth";
import toast, { Toaster } from 'react-hot-toast';
import confetti from 'canvas-confetti';

export default function DailyChallenges() {
  const [userRole, setUserRole] = useState("student");
  const [stats, setStats] = useState({ streak: 0, xp: 0, level: 1, stars: 0 });
  const [trivia, setTrivia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [claimedToday, setClaimedToday] = useState(false);

  useEffect(() => {
    try {
      const token = getToken();
      if (token) setUserRole(jwtDecode(token).role || "student");
    } catch (err) {}
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [statsRes, triviaRes] = await Promise.all([
        api.get("/gamification/stats"),
        api.get("/ai/daily-trivia")
      ]);
      
      setStats(statsRes.data);
      setTrivia(triviaRes.data);
    } catch (err) {
      console.error(err);
      toast.error("Couldn't load mission details!");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (option) => {
    if (isCorrect !== null) return; // Block multiple submissions
    setSelectedAnswer(option);
  };

  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#FFD700', '#FFA500', '#9333EA', '#4ADE80']
    });
  };

  const verifyChallenge = async () => {
    if (!selectedAnswer || isCorrect !== null) return;
    setSubmitting(true);

    const isRight = selectedAnswer === trivia.correct_option;
    setIsCorrect(isRight);

    if (isRight) {
      triggerConfetti();
      toast.success("Bulls-eye! That is correct! 🎯");
      
      try {
        // Hit the streak backend endpoint!
        const res = await api.post("/gamification/daily-challenge");
        if (res.data.success) {
          setStats(prev => ({
            ...prev,
            streak: res.data.streak,
            xp: res.data.xp,
            level: res.data.level,
            stars: prev.stars + 5
          }));
          setClaimedToday(true);
          toast.success("Streak maintained! +50 XP, +5 Stars!");
        } else {
          toast.success("Correct! (Challenge reward already claimed today)");
          setClaimedToday(true);
        }
      } catch (err) {
        console.error(err);
      }
    } else {
      toast.error("Not quite! Check out the fun fact!");
    }
    setSubmitting(false);
  };

  return (
    <div className="flex h-screen bg-[#FFFBFA] dark:bg-slate-950 overflow-hidden relative font-['Inter',sans-serif]">
      <Toaster position="top-center" />
      
      <main className="flex-1 flex flex-col h-full relative overflow-y-auto pb-20 z-10 min-w-0">
        
        {/* Warm aesthetic patterns */}
        <div className="absolute top-[-15%] right-[-10%] w-[35vw] h-[35vw] bg-orange-400/10 rounded-full blur-[100px] pointer-events-none animate-pulse" />
        <div className="absolute bottom-[10%] left-[-5%] w-[30vw] h-[30vw] bg-yellow-300/10 rounded-full blur-[90px] pointer-events-none" />

        {/* Top Gamification Bar */}
        <header className="h-24 px-8 flex items-center justify-between bg-white/40 dark:bg-slate-900/40 backdrop-blur-md relative z-20 flex-shrink-0">
          <div>
            <h1 className="text-2xl font-black text-orange-900 dark:text-orange-400 tracking-tight flex items-center gap-2">
              Daily Quest Chamber <Sparkles size={20} className="text-yellow-500 fill-yellow-500" />
            </h1>
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 mt-0.5">Complete your daily brain challenge to boost your level!</p>
          </div>

          <div className="flex items-center gap-4">
            <motion.div whileHover={{scale:1.05}} className="flex items-center gap-2 bg-orange-100 text-orange-700 font-black px-4 py-2.5 rounded-2xl border-2 border-orange-200 shadow-md">
              <Flame size={20} fill="currentColor" className="animate-bounce" />
              <span>{stats.streak} Day Streak</span>
            </motion.div>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center p-6">
          {loading ? (
            <div className="flex flex-col items-center gap-4">
              <motion.div animate={{rotate: 360}} transition={{repeat:Infinity, duration: 1.2, ease: "linear"}} className="w-14 h-14 border-8 border-orange-200 border-t-orange-500 rounded-full" />
              <p className="text-sm font-black text-orange-700 italic">Generating brain quest matrix...</p>
            </div>
          ) : (
            <div className="w-full max-w-3xl grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              
              {/* Core Trivia Card */}
              <motion.div 
                initial={{opacity:0, y:30}}
                animate={{opacity:1, y:0}}
                className="lg:col-span-8 bg-white dark:bg-slate-900 rounded-[3rem] border-b-[12px] border-orange-100 dark:border-slate-800 p-8 md:p-10 shadow-2xl space-y-8 relative group overflow-hidden"
              >
                <div className="absolute right-[-10%] top-[-10%] w-32 h-32 bg-orange-100 rounded-full blur-xl opacity-40 group-hover:scale-125 transition-transform" />

                {/* Header Tag */}
                <div className="flex justify-between items-center">
                  <span className="px-4 py-2 bg-orange-50 dark:bg-orange-950 text-orange-600 text-xs font-black rounded-full border border-orange-100 dark:border-orange-900 uppercase tracking-widest flex items-center gap-1.5">
                    <Zap size={12} fill="currentColor" /> Global Mind Quest
                  </span>
                  <span className="text-xs font-black text-emerald-500 uppercase flex items-center gap-1">
                    <Star size={12} fill="currentColor" /> +50 XP Available
                  </span>
                </div>

                {/* Question */}
                <h2 className="text-2xl md:text-3xl font-black text-slate-800 dark:text-white tracking-tight leading-snug relative">
                  "{trivia.question}"
                </h2>

                {/* Options */}
                <div className="space-y-4 relative">
                  {trivia.options.map((opt, idx) => {
                    const isSelected = selectedAnswer === opt;
                    const isRightOpt = opt === trivia.correct_option;
                    
                    let borderStyles = "border-slate-200 hover:border-orange-300 hover:bg-orange-50/30";
                    let textStyles = "text-slate-700 dark:text-slate-200";
                    let bgStyles = "bg-white dark:bg-slate-900";
                    
                    if (isSelected) {
                      borderStyles = "border-orange-500 bg-orange-50/50 dark:bg-orange-950/30";
                    }
                    
                    if (isCorrect !== null) {
                      if (isRightOpt) {
                        borderStyles = "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30";
                        textStyles = "text-emerald-700 dark:text-emerald-400";
                      } else if (isSelected && !isRightOpt) {
                        borderStyles = "border-red-500 bg-red-50 dark:bg-red-950/30";
                        textStyles = "text-red-700 dark:text-red-400";
                      }
                    }

                    return (
                      <motion.button
                        key={idx}
                        whileHover={isCorrect === null ? { scale: 1.02, x: 4 } : {}}
                        onClick={() => handleAnswerSelect(opt)}
                        disabled={isCorrect !== null}
                        className={`w-full text-left px-6 py-4 rounded-2xl border-b-4 font-bold transition-all flex items-center justify-between shadow-md active:translate-y-1 active:border-b-0 ${bgStyles} ${borderStyles} ${textStyles}`}
                      >
                        <span className="flex items-center gap-4">
                          <span className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-black shadow-sm border ${
                            isSelected ? "bg-orange-600 text-white" : "bg-slate-50 text-slate-400"
                          }`}>
                            {String.fromCharCode(65 + idx)}
                          </span>
                          {opt}
                        </span>
                        
                        {isCorrect !== null && isRightOpt && <CheckCircle2 className="text-emerald-500 flex-shrink-0" size={20} />}
                        {isCorrect !== null && isSelected && !isRightOpt && <XCircle className="text-red-500 flex-shrink-0" size={20} />}
                      </motion.button>
                    );
                  })}
                </div>

                {/* Action / Reveal Section */}
                <AnimatePresence mode="wait">
                  {isCorrect === null ? (
                    <motion.button
                      initial={{opacity:0}}
                      animate={{opacity:1}}
                      exit={{opacity:0}}
                      disabled={!selectedAnswer || submitting}
                      onClick={verifyChallenge}
                      className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-black py-4 rounded-2xl border-b-8 border-orange-800 shadow-lg active:translate-y-1 active:border-b-0 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {submitting ? <Loader2 className="animate-spin" /> : <Trophy size={18} fill="currentColor" />}
                      Lock in Final Answer
                    </motion.button>
                  ) : (
                    <motion.div 
                      initial={{opacity:0, height: 0}}
                      animate={{opacity:1, height: "auto"}}
                      className={`p-6 rounded-3xl border-b-4 ${isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'} relative overflow-hidden`}
                    >
                      <div className="relative z-10">
                        <p className="text-xs uppercase font-black text-slate-400 tracking-wider mb-1">🧠 Mind Expansion Fact</p>
                        <p className={`text-sm font-bold leading-relaxed ${isCorrect ? 'text-emerald-800' : 'text-amber-900'}`}>
                          {trivia.fun_fact}
                        </p>
                        
                        <button
                          onClick={loadInitialData}
                          className="mt-4 bg-white px-5 py-2 rounded-xl font-black text-xs flex items-center gap-2 shadow border border-black/5 hover:bg-slate-50 transition-colors"
                        >
                          Try New Quest <ArrowRight size={12} />
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Stats Sidebar */}
              <div className="lg:col-span-4 space-y-6 flex flex-col">
                
                {/* XP Box */}
                <div className="bg-white dark:bg-slate-900 p-6 rounded-[2.5rem] border-b-8 border-slate-200 dark:border-slate-800 shadow-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center text-yellow-600">
                      <Trophy size={20} fill="currentColor" />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest font-black text-slate-400">Current Stats</p>
                      <p className="text-lg font-black text-slate-800 dark:text-white">Level {stats.level} Explorer</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between text-xs font-black">
                      <span className="text-slate-400">Global XP</span>
                      <span className="text-orange-600">{stats.xp} XP</span>
                    </div>
                    <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden p-1 border border-slate-200">
                      <motion.div 
                        initial={{width: 0}}
                        animate={{width: `${stats.xp % 100}%`}}
                        className="h-full bg-orange-500 rounded-full" 
                      />
                    </div>
                    <p className="text-[10px] italic font-semibold text-slate-400">{100 - (stats.xp % 100)} XP to level up!</p>
                  </div>
                </div>

                {/* Daily Rules */}
                <div className="bg-gradient-to-br from-indigo-600 to-violet-700 text-white p-6 rounded-[2.5rem] border-b-8 border-indigo-900 shadow-xl relative overflow-hidden group">
                  <div className="absolute inset-0 bg-[url('https://transparenttextures.com/patterns/cubes.png')] opacity-10" />
                  <h4 className="font-black text-lg tracking-tight mb-3 relative">Flame Streak Rules 🔥</h4>
                  <ul className="space-y-2.5 text-xs font-bold text-indigo-100 relative">
                    <li className="flex gap-2"><span>✨</span> Get +50 XP every single consecutive day!</li>
                    <li className="flex gap-2"><span>⚡</span> Missing one day resets your Flame counter.</li>
                    <li className="flex gap-2"><span>🏆</span> Every 3rd and 7th day yields Epic Badges!</li>
                  </ul>
                </div>

              </div>

            </div>
          )}
        </div>
      </main>
    </div>
  );
}
